/**
 * VRG-378: what the reporter was running, captured at the moment they report it.
 *
 * The whole argument for this file is an asymmetry: every value below is trivially
 * knowable while the person is still on the page, and completely unrecoverable an
 * hour later. "The certificate page looked wrong yesterday" cannot be resolved by
 * asking the reporter for more detail, because the missing information was never
 * theirs to give — it is which build they were served and which deployment they
 * were talking to.
 *
 * NONE of this is ever a form field. Asking a user which bundle hash they are on
 * is the canonical example of a question a product should answer for itself, the
 * same reasoning that already keeps `userAgent` off the intake form.
 *
 * Everything here is best-effort and every accessor is guarded. A report is worth
 * having with a partial payload and worth nothing if collecting the payload threw
 * on the way to sending it — so a failure degrades to "not reported", never to a
 * lost report.
 */

import type { ISupportClientContext } from '@repo/types'

import { getPreviousRoute } from './routeHistory'

export interface BuildContext {
  /** Which deployment this bundle talks to. A brand on the dev bundle is itself the bug. */
  environment: string | null
  /** `<package version>+<short sha>`, stamped by vite's `define`. */
  appVersion: string | null
  clientContext: ISupportClientContext
}

/**
 * `__VERA_BUILD__` is a build-time textual substitution, so in any context vite
 * did not compile — a jest run, a storybook without the define — the identifier
 * is not merely undefined, it does not EXIST, and touching it is a ReferenceError
 * rather than `undefined`. Hence `typeof`, which is the one form that is legal on
 * an undeclared name.
 */
const buildStamp = (): string | null => {
  try {
    return typeof __VERA_BUILD__ === 'string' && __VERA_BUILD__.length ? __VERA_BUILD__ : null
  } catch {
    return null
  }
}

const environment = (): string | null => {
  const fromEnv = import.meta.env.VITE_ENVIRONMENT
  if (typeof fromEnv === 'string' && fromEnv.trim().length) return fromEnv.trim()
  // A bundle with no VITE_ENVIRONMENT is not nothing — it is almost certainly a
  // local build, and saying so is more useful than a null the reader has to guess at.
  return import.meta.env.DEV ? 'development' : null
}

/**
 * The route the reporter was on, as an absolute URL.
 *
 * Takes an explicit pathname because the intake opens as a modal OVER a
 * background route: by the time the form submits, `window.location` is
 * `/support/new`, which is the one page in the app that is never the answer.
 */
export const pageUrlFor = (pathname?: string | null): string | null => {
  try {
    if (pathname) return `${window.location.origin}${pathname}`
    return window.location.href
  } catch {
    return null
  }
}

/**
 * Capture everything worth knowing about this client, right now.
 *
 * `viewport` is here rather than in a column because a layout bug is frequently
 * only true at one width, and a report that says "the table overflowed" is a
 * different bug at 1280 than at 3440 — but nobody has ever wanted to FILTER a
 * queue by viewport, which is the test that decides column vs jsonb.
 */
export const getBuildContext = (): BuildContext => {
  const clientContext: ISupportClientContext = {}

  try {
    clientContext.viewport = `${window.innerWidth}x${window.innerHeight}`
  } catch {
    /* not worth a failed report */
  }

  try {
    // Correlating a report against a log line needs the reporter's clock, not the
    // server's. `timeZone` is the durable form; a UTC offset changes twice a year.
    clientContext.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? null
  } catch {
    /* not worth a failed report */
  }

  try {
    clientContext.platform = 'web'
    clientContext.osVersion = navigator?.platform ?? null
  } catch {
    /* not worth a failed report */
  }

  try {
    // VRG-721. A wrong-language bug ("the date reads 10/19 for a French user") is
    // undiagnosable without it, and like the viewport it is never worth a form field.
    clientContext.language = navigator?.language ?? null
  } catch {
    /* not worth a failed report */
  }

  try {
    // VRG-721. The page before the one being reported — pathname only.
    const previous = getPreviousRoute()
    if (previous) clientContext.previousRoute = previous
  } catch {
    /* not worth a failed report */
  }

  return {
    environment: environment(),
    appVersion: buildStamp(),
    clientContext,
  }
}
